import type { NavigationIcon } from './shared/navigation-config'
import { Fragment, type ReactNode } from 'react'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { SectionIcon } from './shared/section-icon'

export type BreadcrumbItem = {
  label: string
  link?: ReactNode
}

export function BreadcrumbTrail(props: { items: BreadcrumbItem[] }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {props.items.map((breadcrumb, index, array) => {
          const isLast = index === array.length - 1

          return (
            <Fragment key={breadcrumb.label}>
              <BreadcrumbItem>
                {breadcrumb.link ? (
                  <BreadcrumbLink
                    asChild
                    className="text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl"
                  >
                    {breadcrumb.link}
                  </BreadcrumbLink>
                ) : (
                  <span className="truncate text-base font-semibold sm:text-xl">{breadcrumb.label}</span>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export function TitleSection(props: {
  icon: NavigationIcon
  title: string
  actions?: ReactNode
  /**
   * Self-contained breadcrumb node. Each section owns its own `<Breadcrumb>` and reads its detail name
   * via `useSuspenseQuery` inside a `<Suspense>` boundary, so SSR streams the section crumb immediately
   * and the detail crumb fills in with no hydration mismatch. Static-crumb leaf routes pass
   * `<BreadcrumbTrail items={...} />`. When absent, falls back to the plain `title` heading.
   */
  breadcrumbsSlot?: ReactNode
}) {
  return (
    <div className="z-40 flex shrink-0 items-center justify-between gap-2 border-b p-3 sm:gap-4 sm:p-5 print:hidden">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <SectionIcon icon={props.icon} className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
        {props.breadcrumbsSlot ?? <h1 className="truncate text-base font-semibold sm:text-xl">{props.title}</h1>}
      </div>
      {props.actions && <div className="flex shrink-0 items-center gap-2 sm:gap-3">{props.actions}</div>}
    </div>
  )
}
